/**
 * Achievement evaluator.
 *
 * `evaluateAchievements(context)` answers one question for every achievement:
 * is its condition satisfied right now. It is pure, takes plain data and
 * returns ids, so the whole table is testable from fixtures with no store, no
 * DOM and no match.
 *
 * Unlocking is deliberately not done here. `hooks.ts` owns the side effects:
 * it builds a context from the stores, asks this function, and records whatever
 * is newly satisfied with a timestamp.
 */

import type { Faction } from '@/engine';
import type { MatchSummary } from '@/app/stores/matchHistory';
import rawAchievements from '@/data/achievements.json';
import {
  CATEGORY_ORDER,
  type Achievement,
  type AchievementCategory,
  type AchievementCondition,
} from './types';

export const allAchievements: Achievement[] = rawAchievements as Achievement[];

const index = new Map<string, Achievement>();
for (const a of allAchievements) index.set(a.id, a);

export function getAchievement(id: string): Achievement | undefined {
  return index.get(id);
}

export function achievementsByCategory(category: AchievementCategory): Achievement[] {
  return allAchievements.filter((a) => a.category === category);
}

/** Every category that has at least one achievement, in display order. */
export function usedCategories(): AchievementCategory[] {
  return CATEGORY_ORDER.filter((c) => allAchievements.some((a) => a.category === c));
}

/** Counters the achievements store keeps, because no other store owned them. */
export interface AchievementCounters {
  packsOpened: number;
  genesisPulled: number;
  tutorialCompleted: boolean;
  /** Factions a mono deck win has been taken with, ever. */
  monoWins: Faction[];
}

export function emptyCounters(): AchievementCounters {
  return { packsOpened: 0, genesisPulled: 0, tutorialCompleted: false, monoWins: [] };
}

export interface AchievementContext {
  /** The match that has just finished, or null when the check is not match driven. */
  match: MatchSummary | null;
  stats: { wins: number; losses: number; gamesPlayed: number; rating: number };
  counters: AchievementCounters;
  equipped: { sleeve: boolean; playmat: boolean; banner: boolean };
  savedDeckCount: number;
  /** Card ids the player owns at least one copy of. */
  ownedCardIds: string[];
  /** Every card id in the set, for the completion check. */
  setCardIds: string[];
}

const ALL_FACTIONS: Faction[] = ['btc', 'eth', 'sol', 'bnb', 'xrp'];

function won(match: MatchSummary | null): boolean {
  return match?.result === 'win';
}

function inRange(value: number | undefined | null, min?: number, max?: number): boolean {
  if (value === undefined || value === null) return false;
  if (min !== undefined && value < min) return false;
  if (max !== undefined && value > max) return false;
  return true;
}

/** Is this one condition satisfied by this context? */
export function evaluateCondition(condition: AchievementCondition, ctx: AchievementContext): boolean {
  switch (condition.kind) {
    case 'stat':
      return ctx.stats[condition.stat] >= condition.min;

    case 'counter': {
      const value =
        condition.counter === 'savedDecks' ? ctx.savedDeckCount : ctx.counters[condition.counter];
      return value >= condition.min;
    }

    case 'tutorialComplete':
      return ctx.counters.tutorialCompleted;

    case 'matchFlag': {
      if (!ctx.match) return false;
      if (condition.win && !won(ctx.match)) return false;
      const want = condition.value ?? true;
      const have = ctx.match[condition.flag];
      // A match that did not report the flag cannot satisfy it either way: an
      // absent field means "the battle screen did not measure this", not false.
      if (have === undefined) return false;
      return have === want;
    }

    case 'matchValue': {
      if (!ctx.match) return false;
      if (condition.win && !won(ctx.match)) return false;
      return inRange(ctx.match[condition.field], condition.min, condition.max);
    }

    case 'monoFactionWin':
      return ctx.counters.monoWins.includes(condition.faction);

    case 'allMonoFactionWins':
      return ALL_FACTIONS.every((f) => ctx.counters.monoWins.includes(f));

    case 'ownEveryCard': {
      if (ctx.setCardIds.length === 0) return false;
      const owned = new Set(ctx.ownedCardIds);
      return ctx.setCardIds.every((id) => owned.has(id));
    }

    case 'equipped':
      return condition.slots.every((slot) => ctx.equipped[slot]);

    default:
      return false;
  }
}

/** Ids of every achievement whose condition holds in this context. */
export function evaluateAchievements(ctx: AchievementContext): string[] {
  return allAchievements.filter((a) => evaluateCondition(a.condition, ctx)).map((a) => a.id);
}

/**
 * Which faction, if exactly one, a finished match's deck was built from. Used
 * to keep the mono win list, which is what the faction achievements read.
 */
export function monoFactionOf(match: MatchSummary): Faction | null {
  const factions = (match.factionsUsed ?? []).filter((f) => f !== 'neutral');
  return factions.length === 1 ? factions[0] : null;
}
